import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sun, Moon, LogOut, User, History, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext/ThemeContext';
import NotificationBell from '../NotificationBell/NotificationBell';

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isWorker, isProvider, isAdmin, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();


  useEffect(() => { setMenuOpen(false); setProfileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (location.pathname === '/login') return null;

  const handleLogout = () => { logout(); setMenuOpen(false); setProfileOpen(false); navigate('/'); };

  let mainLinks = [];
  if (isAdmin) mainLinks = [{ path: '/admin', label: 'Admin Dashboard' }];
  else if (isWorker) mainLinks = [{ path: '/', label: 'Home' }, { path: '/worker', label: 'Find Work' }, { path: '/about', label: 'About' }];
  else if (isProvider) mainLinks = [{ path: '/', label: 'Home' }, { path: '/provider', label: 'Post Work' }, { path: '/about', label: 'About' }];
  else mainLinks = [{ path: '/', label: 'Home' }, { path: '/about', label: 'About' }];

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b"
      style={{
        background: 'var(--navbar-bg)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderColor: 'var(--navbar-border)'
      }}>

      <div className="section-container-wide">
        <div className="flex items-center justify-between h-16">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 focus:outline-none" aria-label="Home">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--primary)' }}>
              <span className="font-bold text-sm" style={{ color: 'var(--primary-foreground)' }}>M</span>
            </div>
            <span className="text-base font-bold tracking-tight hidden sm:inline" style={{ color: 'var(--foreground)' }}>MattersUrSkills</span>
          </Link>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-1">
            {mainLinks.map((link) => (
              <Link key={link.path} to={link.path}
                className={`relative px-3.5 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${isActive(link.path) ? '' : 'hover:bg-[var(--accent)]'}`}
                style={{ color: isActive(link.path) ? 'var(--foreground)' : 'var(--muted-foreground)', fontWeight: isActive(link.path) ? '600' : '500' }}>
                {link.label}
                {isActive(link.path) && (
                  <motion.div layoutId="nav-indicator" className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full"
                    style={{ background: 'var(--foreground)' }} transition={{ type: 'spring', stiffness: 400, damping: 30 }} />
                )}
              </Link>
            ))}
          </div>

          {/* Desktop Right */}
          <div className="hidden md:flex items-center gap-2">
            {isAuthenticated && <NotificationBell />}
            <button onClick={toggleTheme} className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:bg-[var(--accent)]" aria-label="Toggle theme">
              <AnimatePresence mode="wait" initial={false}>
                {isDark ? (
                  <motion.div key="sun" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
                    <Sun className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
                  </motion.div>
                ) : (
                  <motion.div key="moon" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
                    <Moon className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>

            {isAuthenticated ? (
              <div className="relative" ref={profileRef}>
                <button onClick={() => setProfileOpen(!profileOpen)} className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-[var(--accent)] transition-all duration-200">
                  <div className="w-8 h-8 rounded-full overflow-hidden border-2 transition-all" style={{ borderColor: 'var(--border)' }}>
                    {user?.profileImage ? (
                      <img src={user.profileImage} alt={user?.name || 'Profile'} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--primary)' }}>
                        <span className="font-bold text-xs" style={{ color: 'var(--primary-foreground)' }}>{(user?.name?.[0] || '?').toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${profileOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--muted-foreground)' }} />
                </button>

                <AnimatePresence>
                  {profileOpen && (
                    <motion.div initial={{ opacity: 0, y: 8, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      transition={{ duration: 0.15 }} className="absolute right-0 mt-2 w-56 rounded-xl shadow-lg border overflow-hidden"
                      style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                      <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
                        <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{user?.name || 'User'}</p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{user?.email || ''}</p>
                      </div>
                      <div className="py-1">
                        <Link to="/profile" onClick={() => setProfileOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-[var(--accent)]" style={{ color: 'var(--foreground)' }}>
                          <User className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} /> My Profile
                        </Link>
                        {isWorker && (
                          <Link to="/work-history" onClick={() => setProfileOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-[var(--accent)]" style={{ color: 'var(--foreground)' }}>
                            <History className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} /> Work History
                          </Link>
                        )}
                      </div>
                      <div className="py-1 border-t" style={{ borderColor: 'var(--border)' }}>
                        <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm transition-colors hover:bg-[var(--accent)]" style={{ color: 'var(--destructive)' }}>
                          <LogOut className="w-4 h-4" /> Log out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="px-3.5 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-[var(--accent)]" style={{ color: 'var(--foreground)' }}>Log in</Link>
                <Link to="/login?tab=register" className="btn-primary text-sm !py-2 !px-4">Get Started</Link>
              </div>
            )}
          </div>

          {/* Mobile */}
          <div className="flex items-center gap-2 md:hidden">
            {isAuthenticated && <NotificationBell />}
            <button onClick={toggleTheme} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-[var(--accent)]" aria-label="Toggle theme">
              {isDark ? <Sun className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} /> : <Moon className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />}
            </button>
            <button onClick={() => setMenuOpen(!menuOpen)} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-[var(--accent)]" aria-label="Menu">
              {menuOpen ? <X className="w-5 h-5" style={{ color: 'var(--foreground)' }} /> : <Menu className="w-5 h-5" style={{ color: 'var(--foreground)' }} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }} className="md:hidden overflow-hidden border-t" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
            <div className="px-4 py-3 space-y-1">
              {mainLinks.map((link) => (
                <Link key={link.path} to={link.path} onClick={() => setMenuOpen(false)}
                  className="block px-3 py-2.5 rounded-lg text-sm font-medium transition-all hover:bg-[var(--accent)]"
                  style={{ color: isActive(link.path) ? 'var(--foreground)' : 'var(--muted-foreground)', background: isActive(link.path) ? 'var(--accent)' : 'transparent' }}>
                  {link.label}
                </Link>
              ))}
              <div className="my-2 border-t" style={{ borderColor: 'var(--border)' }} />
              {isAuthenticated ? (
                <>
                  <Link to="/profile" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-[var(--accent)]" style={{ color: 'var(--foreground)' }}>
                    <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0">
                      {user?.profileImage ? <img src={user.profileImage} alt="" className="w-full h-full object-cover" /> :
                        <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--primary)' }}><span className="font-bold text-xs" style={{ color: 'var(--primary-foreground)' }}>{(user?.name?.[0] || '?').toUpperCase()}</span></div>}
                    </div>
                    My Profile
                  </Link>
                  {isWorker && <Link to="/work-history" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-[var(--accent)]" style={{ color: 'var(--foreground)' }}>Work History</Link>}
                  <button onClick={handleLogout} className="w-full px-3 py-2.5 rounded-lg text-sm font-medium text-left hover:bg-[var(--accent)]" style={{ color: 'var(--destructive)' }}>Log out</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-[var(--accent)]" style={{ color: 'var(--foreground)' }}>Log in</Link>
                  <Link to="/login?tab=register" onClick={() => setMenuOpen(false)} className="block btn-primary text-center mt-2">Get Started</Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
